<script setup lang="ts">
import { AliyahType, BarMitzvahType, HaftarahType, ParashahType, SentenceGroupType, SentenceType } from 'types/types'
import { computed, onMounted, reactive, toRef, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { type PracticeListItem, useTextCategories } from '@/composables/useTextCategories'
import store from '@/store'

const logger = useLogger()
const ALIYOT_LABEL = 'Aliyot'
const HAFTAROT_LABEL = 'Haftarot'

interface Props {
  aliyah?: AliyahType | null
  barMitzvah: BarMitzvahType
  haftarah?: HaftarahType | null
  sentenceGroup?: SentenceGroupType | null
  sentences?: Array<SentenceType> | null
}

const props = withDefaults(defineProps<Props>(), {
  aliyah: null,
  haftarah: null,
  sentenceGroup: null,
  sentences: null,
})

interface PracticeSelect {
  parshiyot: Array<ParashahType> | null // null will show all parshiyot in the dropdown; [] will show none
  aliyot: Array<AliyahType> | null // null will show all aliyot in the dropdown; [] will show none
  haftarot: Array<HaftarahType> | null // null will show all haftarot in the dropdown; [] will show none
  sentenceGroups: Array<SentenceGroupType> | null // null will show all sentence groups in the dropdown; [] will show none

  // these are all internally watched and cause an update to the associated
  // props so that parent is notified of choices made.
  selectedBarMitzvah: BarMitzvahType | null
  selectedAliyah: AliyahType | null
  selectedHaftarah: HaftarahType | null
  selectedSentenceGroup: SentenceGroupType | null

  selectedPracticeListItem: PracticeListItem | null
  mySentences: Array<SentenceType> | null // managed by watchers for aliyahSentences, haftarahSentences, sentenceGroupSentences
}

const state = reactive<PracticeSelect>({
  aliyot: null,
  haftarot: null,
  mySentences: null,
  parshiyot: null,

  selectedAliyah: props.aliyah,
  selectedBarMitzvah: props.barMitzvah,
  selectedHaftarah: props.haftarah,
  selectedPracticeListItem: null,

  selectedSentenceGroup: props.sentenceGroup,

  sentenceGroups: null,
})

const aliyahSentences = computed<Array<SentenceType>>(() => {
  if (!state.selectedAliyah) {
    return []
  }
  return store.getters.student.getAliyahSentences(state.selectedAliyah.id)
})

const { formattedAliyot, formattedHaftarot, formattedSentenceGroupsByCategory } = useTextCategories(toRef(props, 'barMitzvah'))

// concatenate aliyot, haftarah and sentence groups into a single array for the dropdown
const combinedListItems = computed<Array<PracticeListItem>>((): Array<PracticeListItem> => {
  const list = [] as Array<PracticeListItem>

  if (formattedAliyot.value.length) {
    list.push({ aliyah: null, haftarah: null, props: { disabled: true }, sentenceGroup: null, title: ALIYOT_LABEL })
    list.push(...formattedAliyot.value)
  }
  if (formattedHaftarot.value.length) {
    list.push({ aliyah: null, haftarah: null, props: { disabled: true }, sentenceGroup: null, title: HAFTAROT_LABEL })
    list.push(...formattedHaftarot.value)
  }
  list.push(...formattedSentenceGroupsByCategory.value)

  return list
})

const haftarahSentences = computed(() => {
  if (!state.selectedHaftarah) {
    return []
  }
  return store.getters.student.getHaftarahSentences(state.selectedHaftarah.reading.id)
})

const sentenceGroupSentences = computed(() => {
  if (!state.selectedSentenceGroup) {
    return []
  }
  return store.getters.student.getSentenceGroupSentences(state.selectedSentenceGroup.reading.id)
})

const emit = defineEmits(['update:sentences', 'update:aliyah', 'update:haftarah', 'update:sentenceGroup'])

onMounted(() => {
  state.selectedBarMitzvah = props.barMitzvah

  watch(
    () => props.aliyah,
    () => {
      logger.debug('PracticeSelect, watch props.aliyah =')
      logger.debug(props.aliyah)
      if (props.aliyah) {
        state.selectedPracticeListItem =
          combinedListItems.value.find((combinedListItem: PracticeListItem): boolean => {
            return !!combinedListItem.aliyah && !!props.aliyah && combinedListItem.aliyah.id === props.aliyah.id
          }) ?? null
      }
    },
  )

  watch(
    () => props.haftarah,
    () => {
      logger.debug('PracticeSelect, watch props.haftarah =')
      logger.debug(props.haftarah)
      if (props.haftarah) {
        state.selectedPracticeListItem =
          combinedListItems.value.find((combinedListItem: PracticeListItem) => {
            return !!combinedListItem.haftarah && !!props.haftarah && combinedListItem.haftarah.reading.id === props.haftarah?.reading.id
          }) ?? null
      }
    },
  )

  watch(
    () => props.sentenceGroup,
    () => {
      logger.debug('PracticeSelect, watch props.sentenceGroup =')
      logger.debug(props.sentenceGroup)
      if (props.sentenceGroup) {
        state.selectedPracticeListItem =
          combinedListItems.value.find((combinedListItem: PracticeListItem) => {
            return (
              !!combinedListItem.sentenceGroup &&
              !!props.sentenceGroup &&
              combinedListItem.sentenceGroup.reading.id === props.sentenceGroup?.reading.id
            )
          }) ?? null
      }
    },
  )

  watch(
    () => state.selectedAliyah,
    () => {
      logger.debug('PracticeSelect, watch state.selectedAliyah =')
      logger.debug(state.selectedAliyah)
      if (state.selectedAliyah) {
        store.dispatch.student.fetchAliyahSentences({
          aliyah: state.selectedAliyah,
        })
      }
      emit('update:aliyah', state.selectedAliyah)
    },
  )

  watch(
    () => state.selectedPracticeListItem,
    () => {
      logger.debug('state.selectedPracticeListItem =')
      logger.debug(state.selectedPracticeListItem)
      if (state.selectedPracticeListItem) {
        state.selectedAliyah = state.selectedPracticeListItem.aliyah
        state.selectedHaftarah = state.selectedPracticeListItem.haftarah
        state.selectedSentenceGroup = state.selectedPracticeListItem.sentenceGroup
      } else {
        state.selectedAliyah = null
        state.selectedHaftarah = null
        state.selectedSentenceGroup = null
        state.mySentences = null
      }
    },
    { immediate: true },
  )

  watch(aliyahSentences, () => {
    if (aliyahSentences.value.length) {
      state.mySentences = aliyahSentences.value
    }
  })

  watch(
    () => state.selectedHaftarah,
    () => {
      logger.debug('PracticeSelect, watch state.selectedHaftarah =')
      logger.debug(state.selectedHaftarah)
      if (state.selectedHaftarah) {
        store.dispatch.student.fetchHaftarahSentences({
          haftarah: state.selectedHaftarah,
        })
      }
      emit('update:haftarah', state.selectedHaftarah)
    },
  )

  watch(haftarahSentences, () => {
    if (haftarahSentences.value.length) {
      state.mySentences = haftarahSentences.value
    }
  })

  watch(
    () => state.selectedSentenceGroup,
    () => {
      logger.debug('PracticeSelect, watch state.selectedSentenceGroup =')
      logger.debug(state.selectedSentenceGroup)
      if (state.selectedSentenceGroup) {
        store.dispatch.student.fetchSentenceGroupSentences({
          sentenceGroup: state.selectedSentenceGroup,
        })
      }
      emit('update:sentenceGroup', state.selectedSentenceGroup)
    },
  )

  watch(
    () => state.mySentences,
    () => {
      if (!state.mySentences || !state.selectedPracticeListItem) {
        emit('update:sentences', [])
        return
      }

      emit('update:sentences', state.mySentences)
    },
  )

  watch(sentenceGroupSentences, () => {
    if (sentenceGroupSentences.value.length) {
      state.mySentences = sentenceGroupSentences.value
    }
  })
})
</script>

<template>
  <v-select
    v-model="state.selectedPracticeListItem"
    clearable
    :items="combinedListItems"
    :label="state.selectedPracticeListItem ? 'Text' : 'Choose a text'"
    :loading="!combinedListItems"
    open-on-clear
    return-object
    variant="underlined"
  />
</template>
